import React from 'react';
import { Check, ExternalLink, Sparkles } from 'lucide-react';
import Button from '../../common/Button';
import ScrollReveal from '../common/ScrollReveal';

export interface ModuleBlockProps {
  id: string;
  category?: string;
  title: string;
  content: string;
  contentList?: string[];
  icon: string;
  direction?: 'left' | 'right';
  badge?: string;
  onRequestDemo?: () => void;
}

export const ModuleBlock: React.FC<ModuleBlockProps> = ({
  id,
  category = 'Hospital Module',
  title,
  content,
  contentList = [],
  icon,
  direction = 'left',
  badge,
  onRequestDemo,
}) => {
  const isReverse = direction === 'right';

  return (
    <section id={id} className="py-16 md:py-24 overflow-hidden scroll-mt-28">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div
          className={`grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center ${
            isReverse ? 'lg:flex-row-reverse' : ''
          }`}
        >
          {/* Screenshot Showcase Column */}
          <div
            className={`lg:col-span-7 ${
              isReverse ? 'lg:order-2' : 'lg:order-1'
            }`}
          >
            <ScrollReveal direction={isReverse ? 'right' : 'left'} delay={200}>
              <div className="relative group">
                {/* Glow backdrop behind screenshot */}
                <div className="absolute -inset-2 bg-gradient-to-r from-[#0D7893]/20 to-teal-400/20 rounded-3xl blur-xl opacity-50 group-hover:opacity-80 transition duration-500" />

                {/* Window Frame Mockup */}
                <div className="relative rounded-2xl md:rounded-3xl bg-white border border-slate-200/90 shadow-2xl shadow-slate-900/10 overflow-hidden">
                  {/* Browser / App Header bar */}
                  <div className="bg-slate-100/90 px-4 py-3 border-b border-slate-200/80 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-rose-400" />
                      <div className="w-3 h-3 rounded-full bg-amber-400" />
                      <div className="w-3 h-3 rounded-full bg-emerald-400" />
                      <span className="ml-2 text-xs font-medium text-slate-500 truncate max-w-[220px] sm:max-w-xs">
                        Divine HMS · {title.replace(/[^\w\s-]/gi, '')}
                      </span>
                    </div>
                    {badge && (
                      <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#0D7893] bg-[#0D7893]/10 px-2.5 py-0.5 rounded-full">
                        <Sparkles className="w-3 h-3" />
                        {badge}
                      </span>
                    )}
                  </div>

                  {/* Screenshot Image */}
                  <div className="relative bg-slate-50 overflow-hidden">
                    <img
                      src={`/img/svg/${icon}`}
                      alt={title}
                      className="w-full h-auto object-cover transform transition-transform duration-500 group-hover:scale-[1.02]"
                      loading="lazy"
                    />
                  </div>
                </div>
              </div>
            </ScrollReveal>
          </div>

          {/* Details / Text Column */}
          <div
            className={`lg:col-span-5 ${
              isReverse ? 'lg:order-1' : 'lg:order-2'
            }`}
          >
            <ScrollReveal direction={isReverse ? 'left' : 'right'} delay={300}>
              <div className="space-y-6">
                {/* Category Pill */}
                <div className="inline-flex items-center gap-2 px-3.5 py-1 rounded-full bg-[#0D7893]/10 text-[#0D7893] text-xs font-bold uppercase tracking-wider border border-[#0D7893]/20">
                  <span>{category}</span>
                </div>

                {/* Title */}
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight leading-tight">
                  {title}
                </h2>

                {/* Content description */}
                <p className="text-base sm:text-lg text-slate-600 leading-relaxed font-normal">
                  {content}
                </p>

                {/* Feature Checkpoints */}
                {contentList.length > 0 && (
                  <div className="space-y-3 pt-2">
                    {contentList.map((item, index) => (
                      <div
                        key={index}
                        className="flex items-start gap-3 p-3 rounded-xl bg-slate-50 hover:bg-[#0D7893]/5 border border-slate-100 hover:border-[#0D7893]/20 transition-all duration-200"
                      >
                        <div className="flex-shrink-0 mt-0.5 w-5 h-5 rounded-full bg-[#0D7893] text-white flex items-center justify-center shadow-sm">
                          <Check className="w-3.5 h-3.5 stroke-[2.5]" />
                        </div>
                        <p className="text-sm font-medium text-slate-700 leading-snug">
                          {item}
                        </p>
                      </div>
                    ))}
                  </div>
                )}

                {/* Action button */}
                <div className="pt-2">
                  <Button
                    size="md"
                    onClick={onRequestDemo}
                    className="gap-2 shadow-sm"
                  >
                    <span>Request Demo for this Module</span>
                    <ExternalLink className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ModuleBlock;
